import { GoogleMap, MarkerClusterer, useJsApiLoader } from '@react-google-maps/api';
import React, { Suspense, useContext, useEffect, useState } from 'react';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { BranchesContext, BranchesContextProps } from '../../context/BranchesContext';
import { NewRequestContext } from '../../context/NewRequestContext';
import { useGetDefaultView } from '../../utils/users-hooks';
import { BranchesStateFilter } from './BranchesStateFilter';
import { Spinner } from 'react-bootstrap';
import { defaultSelectedBranch, defaultSelectedClient } from '../../utils/Enums/NewRequest';
import { useGetNewRequestBranchesByClient } from '../../utils/clients-hooks';
import { OptimizedMarkers } from './OptimizedMarkers';

const containerStyle = {
    width: '100%',
    height: '100%',
    marginBottom: '20px',
};

const clusterStyles = [
    {
        textColor: 'white',
        textSize: 18,
        textStyle: 'bold',
        // anchorText: [2, -12] as [number, number],
        anchorText: [-10, -12] as [number, number],
        anchorIcon: [0, 0] as [number, number],
        url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png',
        height: 80,
        width: 80,
    },
    {
        textColor: 'white',
        textSize: 18,
        textStyle: 'bold',
        // anchorText: [2, -12] as [number, number],
        anchorText: [-10, -12] as [number, number],
        anchorIcon: [0, 0] as [number, number],
        url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m4.png',
        height: 80,
        width: 80,
    },
    {
        textColor: 'white',
        textSize: 18,
        textStyle: 'bold',
        // anchorText: [2, -12] as [number, number],
        anchorText: [-10, -12] as [number, number],
        anchorIcon: [0, 0] as [number, number],
        url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m3.png',
        height: 80,
        width: 80,
    },
    {
        textColor: 'white',
        textSize: 18,
        textStyle: 'bold',
        // anchorText: [2, -12] as [number, number],
        anchorText: [-10, -12] as [number, number],
        anchorIcon: [0, 0] as [number, number],
        url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m5.png',
        height: 80,
        width: 80,
    },
];

export const Map = () => {
    const timestamp = new Date().getTime();
    const apiKey = `${process.env.REACT_APP_GOOGLE_API_KEY}`;
    const [readDefaultView] = useAsyncResource(useGetDefaultView, []);
    const branchesContext = useContext(BranchesContext) as BranchesContextProps & { mapBranches?: any[] };
    const { setNewMarker, newMarker, setSelectedBranches, setLastSelectedBranch } = branchesContext;
    const mapBranches = branchesContext.mapBranches ?? branchesContext.branches;

    const { onLocationClick, onBranchSelect, onClientSelect } = useContext(NewRequestContext);
    // const [map, setMap] = useState({} as google.maps.Map);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [isNewMarkerVissible, setIsNewMarkerVissible] = useState(false);
    const [mapKey] = useState(timestamp);
    const defaultView = readDefaultView();
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: apiKey,
        // libraries: ['places'], // Include any required libraries here
    });
    const [mapReadyForInfoBox, setMapReadyForInfoBox] = useState(false);

    useEffect(() => {
        // if (map && Boolean(Object.keys(newMarker).length > 0)) {
        //     map.panTo(newMarker);
        //     map.setZoom(18)
        // }
        if (map && newMarker?.lat && newMarker?.lng) {
            setMapReadyForInfoBox(false); // reset
            // Delay only the first time to avoid initial override
            setTimeout(() => {
                map.panTo(newMarker);
                map.setZoom(18);
                setTimeout(() => {
                    setMapReadyForInfoBox(true);
                }, 100); // Adjust if needed
            }, 0); // 100ms is usually enough to allow map to "settle"
        }
    }, [newMarker]);

    const onMapLoad = (mapInstance: google.maps.Map) => {
        setMap(mapInstance);

        if (defaultView?.center) {
            mapInstance.panTo(defaultView.center);
            if (defaultView.zoom) {
                mapInstance.setZoom(defaultView.zoom);
            }
        }
    };

    if (!isLoaded) {
        return <Spinner animation='border' />;
    }

    const onRightMapClick = (e: google.maps.MapMouseEvent) => {
        const { latLng } = e;
        onClientSelect(defaultSelectedClient);
        onBranchSelect(defaultSelectedBranch);
        resourceCache(useGetNewRequestBranchesByClient).clear();
        setIsNewMarkerVissible(true);

        if (latLng) {
            setNewMarker({ lat: latLng.lat(), lng: latLng.lng() });
            onLocationClick(String(latLng.lat()), String(latLng.lng()));
        }
    };

    return (
        <div className='d-flex w-100 h-100'>
            <GoogleMap
                key={mapKey}
                mapContainerStyle={containerStyle}
                onRightClick={onRightMapClick}
                onClick={() => {
                    setSelectedBranches([]);
                    setLastSelectedBranch(null);
                    localStorage.removeItem('selectedBranch');
                    localStorage.removeItem('markerID');
                }}
                mapTypeId={'satellite'}
                onLoad={onMapLoad}
                options={{disableDefaultUI:false}}
                // onLoad={setMap}
                // {...defaultPosition}
            >
                {/* maxZoom={10} */}
                <MarkerClusterer
                    styles={clusterStyles}
                    averageCenter={false}
                    // gridSize={65}
                    maxZoom={10}
                    // onClick={onClusterClick}
                    options={{
                        gridSize: 50, // Smaller grid size for tighter clustering
                        averageCenter: false,
                        zoomOnClick: false,
                        ignoreHidden: false,
                    }}
                >
                    {(clusterer) => {
                        return (
                            <Suspense fallback={<Spinner animation='border' />}>
                                {/* <Markers
                                    {...{ isNewMarkerVissible, setIsNewMarkerVissible, clusterer }}
                                    markers={branches}
                                /> */}
                                <OptimizedMarkers
                                    markers={mapBranches}
                                    clusterer={clusterer}
                                    isNewMarkerVissible={isNewMarkerVissible}
                                    setIsNewMarkerVissible={setIsNewMarkerVissible}
                                    mapReadyForInfoBox={mapReadyForInfoBox}
                                />
                            </Suspense>
                        );
                    }}
                </MarkerClusterer>
            </GoogleMap>
            <BranchesStateFilter />
        </div>
    );
};
